import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Trophy, Users, Calendar, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function PublicGiveaway() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const ref_code = params.get("ref") || null;
  const [g, setG] = useState(null);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    api.get(`/public/giveaways/${id}`).then(({ data }) => setG(data)).catch(() => setG(false));
    if (ref_code) api.post(`/giveaways/${id}/referral/visit`, { ref_code }).catch(() => {});
  }, [id, ref_code]);

  const enter = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      const { data } = await api.post(`/public/giveaways/${id}/enter`, {
        email: fd.get("email"),
        name: fd.get("name"),
        ref_code,
      });
      setSubmitted(data);
      toast.success("Entry confirmed!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (g === false) return <div className="min-h-screen flex items-center justify-center text-zinc-500 text-sm">Giveaway not found.</div>;
  if (!g) return null;

  const shareUrl = submitted ? `${window.location.origin}/g/${id}?ref=${submitted.ref_code}` : "";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-2">
          <img src="/assets/app-icon-1200.png" alt="SweepLaunch" className="size-7 rounded-md object-cover" />
          <span className="font-display font-bold text-lg tracking-tight">SweepLaunch</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-white border border-zinc-200 rounded-md p-8 md:p-10">
          <div className="flex items-center gap-2">
            <Trophy className="size-5 text-emerald-600" strokeWidth={1.5} />
            <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-600 font-semibold">Official Sweepstakes</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-3" data-testid="public-giveaway-title">{g.title}</h1>
          <p className="text-zinc-600 mt-3 leading-relaxed">{g.description}</p>

          <div className="grid grid-cols-3 gap-3 mt-6 border-t border-zinc-100 pt-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Prize</div>
              <div className="font-display text-2xl font-bold tabular mt-1">${g.prize_value.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Entries</div>
              <div className="font-display text-2xl font-bold tabular mt-1">{g.total_entries.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold">Ends</div>
              <div className="font-display text-lg font-bold mt-1">{g.end_date}</div>
            </div>
          </div>

          {!submitted ? (
            <form onSubmit={enter} className="mt-8 border-t border-zinc-100 pt-8 space-y-3">
              <div className="text-xs text-zinc-500">No purchase necessary. Free AMOE entry — required by law.</div>
              <input name="name" placeholder="Your name" data-testid="public-name-input" className="w-full border border-zinc-200 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 focus:outline-none" />
              <input name="email" type="email" required placeholder="you@example.com" data-testid="public-email-input" className="w-full border border-zinc-200 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 focus:outline-none" />
              <button type="submit" data-testid="public-enter-button" className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors">
                Enter to win
              </button>
              {ref_code && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-3 py-2 mt-2">You were referred by a friend — they'll earn 5 bonus entries.</div>}
            </form>
          ) : (
            <div className="mt-8 border-t border-zinc-100 pt-8 space-y-4" data-testid="public-confirmation">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle2 className="size-5" strokeWidth={1.5} /><span className="font-display font-bold text-lg">Entry confirmed!</span>
              </div>
              <div className="bg-emerald-50 border border-emerald-100 rounded-md p-4 text-sm">
                <div><span className="text-zinc-500">Confirmation ID:</span> <span className="font-mono font-medium">{submitted.confirmation_id}</span></div>
                <div className="mt-1"><span className="text-zinc-500">Entries credited:</span> <span className="font-medium">{submitted.entries_count}</span></div>
                <div className="mt-1 text-xs text-zinc-500">Save this ID. A confirmation email has been sent.</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">Share & earn 5 bonus entries per friend</div>
                <div className="flex gap-2">
                  <input readOnly value={shareUrl} className="flex-1 border border-zinc-200 rounded-md px-3 py-2 text-xs font-mono bg-zinc-50" data-testid="share-url-input" />
                  <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied!"); }} data-testid="copy-share-button" className="border border-zinc-200 px-3 py-2 rounded-md text-xs font-medium hover:bg-zinc-50">Copy</button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="text-center text-xs text-zinc-500 mt-6">
          Powered by <span className="font-semibold text-zinc-700">SweepLaunch</span> · No purchase necessary
        </div>
      </main>
    </div>
  );
}
